<template>
  <div class="friction-angle-viz">
    <!-- 顶部核心数据 -->
    <div class="viz-header-compact" >
      <div class="stat-group">
        <div class="stat-pill">
          <label>当前倾角 θ</label>
          <span>{{ angle.toFixed(1) }}°</span>
        </div>
        <div class="stat-pill highlight">
          <label>静摩擦角 φ</label>
          <span>{{ frictionAngle.toFixed(1) }}°</span>
        </div>
      </div>
      <div class="formula-chip" v-latex>$tan φ = μ ({{ mu.toFixed(2) }})$</div>
    </div>

    <!-- 3D 仿真舞台 -->
    <div class="main-stage-wrapper" ref="canvasContainer">
      <div class="simulation-hud">
        <div v-if="angle > frictionAngle" class="status-badge sliding">
          <i class="el-icon-loading"></i> 物体下滑中
        </div>
        <div v-else class="status-badge static">
          <i class="el-icon-lock"></i> 静止平衡
        </div>
      </div>
    </div>

    <!-- 控制面板 -->
    <div class="compact-controls">
      <div class="control-grid">
        <div class="control-item">
          <div class="label-row">
            <span class="label">调节斜面坡度 (θ)</span>
            <span class="value">{{ angle }}°</span>
          </div>
          <el-slider v-model="angle" :min="0" :max="60" :step="0.5" size="small"></el-slider>
        </div>
        <div class="control-item">
          <div class="label-row">
            <span class="label">表面粗糙度 (μ)</span>
            <span class="value">{{ mu }}</span>
          </div>
          <el-slider v-model="mu" :min="0.1" :max="1.0" :step="0.05" size="small"></el-slider>
        </div>
      </div>

      <!-- 行 2: 动作按钮 -->
      <div class="action-row">
         <el-button type="primary" size="small" plain @click="resetPos" class="reset-btn" icon="el-icon-refresh-right">重置物体位置</el-button>
      </div>

      <div class="discovery-card-compact" v-latex>
        <i class="el-icon-info"></i>
        <p><strong>物理规律：</strong>摩擦角是物体在斜面上保持静止的临界倾角。当 <strong>θ > arctan(μ)</strong> 时，物体将由静止转为滑动。</p>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'FrictionAngleViz',
  props: { knowledgePoint: String },
  data() {
    return {
      angle: 20,
      mu: 0.45,
      boxOffset: 0,
    }
  },
  computed: {
    frictionAngle() { return Math.atan(this.mu) * 180 / Math.PI; }
  },
  watch: {
    angle(val) { if (val === 0) this.boxOffset = 0; }
  },
  mounted() { this.initThree(); },
  beforeDestroy() {
    this.running = false;
    if (this.frameId) cancelAnimationFrame(this.frameId);
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
      this.renderer = null;
    }
  },
  methods: {
    initThree() {
      const container = this.$refs.canvasContainer;
      const width = container.clientWidth;
      const height = container.clientHeight;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf8fafc);

      this.camera = new THREE.PerspectiveCamera(40, width / height, 1, 1000);
      this.camera.position.set(150, 80, 180);
      this.camera.lookAt(0, 20, 0);

      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(50, 100, 50);
      this.scene.add(light);
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.6));

      // Incline group
      this.inclineGroup = new THREE.Group();
      this.scene.add(this.inclineGroup);

      // Floor/Ramp
      const rampGeo = new THREE.BoxGeometry(200, 4, 60);
      const rampMat = new THREE.MeshPhongMaterial({ color: 0x1e293b });
      const ramp = new THREE.Mesh(rampGeo, rampMat);
      ramp.position.x = 0;
      this.inclineGroup.add(ramp);

      // Cube (Object)
      const boxGeo = new THREE.BoxGeometry(20, 15, 20);
      const boxMat = new THREE.MeshPhongMaterial({ color: 0x2563eb });
      this.box = new THREE.Mesh(boxGeo, boxMat);
      this.box.position.y = 9.5; 
      this.inclineGroup.add(this.box);

      // Ground (Static)
      const groundGeo = new THREE.PlaneGeometry(1000, 1000);
      const groundMat = new THREE.MeshPhongMaterial({ color: 0xe2e8f0 });
      const ground = new THREE.Mesh(groundGeo, groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = -2;
      this.scene.add(ground);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(this.renderer.domElement);

      this.running = true;
      this.animate();
    },
    animate() {
      if (this.frameId) cancelAnimationFrame(this.frameId);
      if (!this.running) return;
      this.frameId = requestAnimationFrame(this.animate);
      
      const rad = this.angle * Math.PI / 180;
      if (this.inclineGroup) this.inclineGroup.rotation.z = rad;

      if (this.angle > this.frictionAngle) {
          this.boxOffset += 0.5;
          if (this.boxOffset > 80) this.boxOffset = 0;
      }
      if (this.box) this.box.position.x = 75 - this.boxOffset;

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },
    resetPos() { this.boxOffset = 0; }
  }
}
</script>

<style scoped lang="scss">
.friction-angle-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; padding-bottom: 10px; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .stat-group { display: flex; gap: 12px; }
  .stat-pill {
    background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 4px 12px;
    label { font-size: 11px; color: #64748b; display: block; margin-bottom: 2px; }
    span { font-size: 15px; color: #1e293b; font-weight: 700; font-family: monospace; }
    &.highlight { border-left: 3px solid #f59e0b; }
  }
  .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 6px 16px; border-radius: 20px; font-weight: 700; font-size: 13px; font-family: Consolas, monospace; }
}

.main-stage-wrapper {
  flex: 1; min-height: 240px; background: #f8fafc; border-radius: 12px; position: relative; overflow: hidden; border: 1.5px solid #e2e8f0;
  .simulation-hud { position: absolute; top: 12px; left: 12px; z-index: 10; }
  .status-badge {
    padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700;
    &.sliding { background: #fee2e2; color: #ef4444; border: 1px solid #fecaca; }
    &.static { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
  }
}

.compact-controls { display: flex; flex-direction: column; gap: 12px; }
.control-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px; background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-item {
    .label-row { display: flex; justify-content: space-between; margin-bottom: 4px; .label { font-size: 13px; color: #64748b; font-weight: 600; } .value { font-size: 13px; color: #2563eb; font-weight: 700; } }
  }
}

.action-row { display: flex; gap: 8px; .el-button { flex: 1; font-weight: 700; height: 36px; } }
.discovery-card-compact { background: #f0f7ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #dbeafe; display: flex; align-items: center; gap: 10px; i { color: #2563eb; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #1e40af; line-height: 1.5; strong { color: #1e3a8a; } } }
</style>
